'use client';

import { useState } from 'react';
import type { TaskInfo } from '@/app/types';
import { getImg2VideoMaxImageCount } from '@/app/utils';
import type { HomeGenerationMode } from '../utils';
import { getSubmitTaskType } from '../utils';

interface UseHomeGenerationFormSubmitOptions {
  handleBeforeProModeSubmit: () => boolean | Promise<boolean>;
  handleCheckSettings: () => void;
  /** 复用 useFormValidation 的 `{模型}_{表单类型}_{原因}` from_page 埋点 */
  handleUpgradeClick: () => void;
  handleReferenceImageOverflowWarning: () => void;
  img2videoModelVersion: string;
  isProMode: boolean;
  isProcessing: boolean;
  mode: HomeGenerationMode;
  shouldUpgradeForModel: boolean;
  shouldWarnReferenceImageOverflow: boolean;
  validationErrorMessage?: string;
  validImageCount: number;
}

export const useHomeGenerationFormSubmit = ({
  handleBeforeProModeSubmit,
  handleCheckSettings,
  handleUpgradeClick,
  handleReferenceImageOverflowWarning,
  img2videoModelVersion,
  isProMode,
  isProcessing,
  mode,
  shouldUpgradeForModel,
  shouldWarnReferenceImageOverflow,
  validationErrorMessage,
  validImageCount,
}: UseHomeGenerationFormSubmitOptions) => {
  const [submittedHomeTasks, setSubmittedHomeTasks] = useState<TaskInfo[]>([]);
  const isSubmitDisabled = !!validationErrorMessage || isProcessing;
  const submitTaskType = getSubmitTaskType(mode, validImageCount);
  const maxImageCount = getImg2VideoMaxImageCount(img2videoModelVersion);

  const handleBeforeSubmit = (): boolean | Promise<boolean> => {
    if (shouldUpgradeForModel) {
      handleUpgradeClick();
      return false;
    }

    if (shouldWarnReferenceImageOverflow) {
      handleReferenceImageOverflowWarning();
    }

    if (isProMode) {
      return handleBeforeProModeSubmit();
    }

    return true;
  };

  const handleSubmitSuccess = (task: TaskInfo) => {
    setSubmittedHomeTasks((tasks) => [...tasks, task]);
    return 'suppressDefaultToast' as const;
  };

  return {
    handleBeforeSubmit,
    handleCheckSettings,
    handleSubmitSuccess,
    isSubmitDisabled,
    maxImageCount,
    submittedHomeTasks,
    submitTaskType,
  };
};
